import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Task as ITask } from 'api/tasks';
import {
  CpBody01Primary,
  CpBody02Primary,
} from 'components/atoms/customer-portal/typography';
import { Skeleton } from 'components/atoms/skeletions';
import { StatusIndicator } from 'components/molecules/status-indicator';
import { useStatusField } from 'data/fields/hooks';
import { useGetDocumentTasksById } from 'hooks/use-documents';
import { useTaskDrawerQueryParamsFromRouter } from 'hooks/use-query-params';
import { useGetDocumentForSpaceTab } from 'queries';

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
  background: var(--cp-white-100);
  padding: var(--cp-spacing-06);
`;

const Title = styled(CpBody02Primary)`
  margin-bottom: var(--cp-spacing-06);
  font-weight: bold;
`;

const TaskWrapper = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  padding: var(--cp-spacing-05) var(--cp-spacing-06);
  padding-left: var(--cp-spacing-03);
  border-bottom: 1px solid var(--cp-border-subtle);
  cursor: pointer;

  ${CpBody01Primary} {
    margin-left: var(--cp-spacing-05);
  }
`;

const Task: React.FC<{ task: ITask; onClick: () => void }> = ({
  task,
  onClick,
}) => {
  const statusField = useStatusField();
  const selectedOption = useMemo(() => {
    const fields = task?.fields ?? [];
    const value = fields.find(
      (field) => field?.fieldId === statusField?.fieldId,
    )?.fieldValue;
    const s = statusField?.metaData?.options?.find(
      (option) => option.value === value,
    );

    return s;
  }, [statusField?.fieldId, statusField?.metaData?.options, task?.fields]);

  return (
    <TaskWrapper onClick={onClick} role="button">
      {selectedOption ? <StatusIndicator status={selectedOption} /> : <></>}
      <CpBody01Primary>{task?.taskName}</CpBody01Primary>
    </TaskWrapper>
  );
};

const StyledSkeleton = styled(Skeleton)`
  height: 48px;
  .ant-skeleton-title {
    width: 100% !important;
    height: 35px;
    margin-bottom: var(--cp-spacing-05) 0;
  }
`;

const TaskSkeletons: React.FC = () => {
  return (
    <>
      <StyledSkeleton active />
      <StyledSkeleton active />
      <StyledSkeleton active />
    </>
  );
};

export const CpDocumentsTaskPane: React.FC<{
  projectId: number;
  spaceTabId: number;
}> = ({ spaceTabId, projectId }) => {
  const doc = useGetDocumentForSpaceTab(projectId, spaceTabId);
  const { data: tasks = [], isLoading } = useGetDocumentTasksById(
    projectId,
    doc?.documentId ?? -1,
  );
  const { openTaskDrawer } = useTaskDrawerQueryParamsFromRouter();

  return (
    <Wrapper>
      <Title>
        <FormattedMessage id="document.tasks" />
      </Title>
      {!isLoading ? (
        tasks.map((task) => (
          <Task
            key={task.taskId}
            task={task}
            onClick={() =>
              openTaskDrawer(task?.project?.projectId, task.taskId)
            }
          />
        ))
      ) : (
        <TaskSkeletons />
      )}
    </Wrapper>
  );
};
